import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ShieldCheck, TrendingUp, Zap, PackageSearch, Layers, Clock, Truck, CheckSquare, Palette, Eye, Scissors, ClipboardCheck, ChevronRight, ArrowRight } from 'lucide-react';
import { categories } from '../lib/categories';
import { cn } from '../lib/utils';
import { BuilderVisual } from '../components/BuilderVisual';
export const Home = () => <div className="flex flex-col w-full" data-id="element-336">
    <HeroSection data-id="element-337" />
    <AudienceSplitSection data-id="element-338" />
    <KitBuilderSection data-id="element-339" />
    <SeasonalSportsSection data-id="element-340" />
    <DealerValueSection data-id="element-341" />
    <QualityProductionSection data-id="element-342" />
    <CategoryGridSection data-id="element-343" />
    <HowItWorksSection data-id="element-344" />
    <FooterCTABanner data-id="element-345" />
  </div>;
const HeroSection = () => <section className="relative bg-white overflow-hidden py-20 lg:py-28 border-b border-gray200" data-id="element-346">
    {/* Background accent */}
    <div className="absolute top-0 right-0 w-1/2 h-full bg-gray50 -z-0 hidden lg:block" data-id="element-347"></div>
    <div className="absolute top-20 right-0 w-1/3 h-1/3 bg-brandRed/5 rounded-full blur-3xl -z-0" data-id="element-348"></div>

    <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10" data-id="element-349">
      <div className="lg:col-span-6 flex flex-col gap-7" data-id="element-350">
        <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-351">
          <span className="w-8 h-px bg-brandRed" data-id="element-352"></span>
          Wholesale Custom Apparel Platform
        </span>
        <h1 className="text-5xl lg:text-6xl xl:text-7xl font-heading font-extrabold text-ink leading-[1.05] tracking-[-0.025em] text-balance" data-id="element-353">
          Wholesale Custom Uniforms Built To Elevate Your Brand
        </h1>
        <p className="text-xl text-gray-600 leading-relaxed max-w-xl" data-id="element-354">
          Private-label uniforms, sublimated apparel, and team gear for dealers,
          resellers, team stores, decorators, sporting goods stores, apparel
          companies, and larger wholesale buyers.
        </p>

        <ul className="flex flex-col gap-3.5 mt-2" data-id="element-355">
          {['Start designs instantly in 3D with Kit Builder', 'Private-label / no-logo production to protect your brand', 'Wholesale pricing built for dealer margins', 'Combined quantity pricing for competitive packages', 'Speed Rush production on many no-button items', 'USA-time support from 9am–10pm EST'].map((point, i) => <li key={i} className="flex items-start gap-3 text-charcoal font-medium text-base" data-id="element-356">
              <CheckCircle2 className="w-5 h-5 text-brandRed shrink-0 mt-0.5" data-id="element-357" />
              <span data-id="element-358">{point}</span>
            </li>)}
        </ul>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4" data-id="element-359">
          <Link to="/kit-builder" className="bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-7 rounded-sm text-center transition-colors shadow-lg shadow-brandRed/20 text-base flex items-center justify-center gap-2 group" data-id="element-360">
            Start Your Design — See It Instantly in 3D
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-361" />
          </Link>
          <Link to="/wholesale-apply" className="bg-white border-2 border-charcoal hover:bg-charcoal hover:text-white text-charcoal font-bold py-3.5 px-7 rounded-sm text-center transition-colors text-base" data-id="element-362">
            Apply for Wholesale Access
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-2" data-id="element-363">
          <span data-id="element-364">Wholesale/account access may be required for ordering.</span>
          <span className="text-gray-300" data-id="element-365">|</span>
          <Link to="/find-a-supplier" className="text-charcoal font-bold hover:text-brandRed transition-colors flex items-center gap-1" data-id="element-366">
            Find a Supplier <ChevronRight className="w-4 h-4" data-id="element-367" />
          </Link>
        </div>
      </div>

      <div className="lg:col-span-6 relative" data-id="element-368">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} data-id="element-369">
          <BuilderVisual theme="light" data-id="element-370" />
        </motion.div>

        {/* Floating stat cards */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-xl border border-gray200 hidden md:flex items-center gap-3" data-id="element-371">
          <div className="w-10 h-10 rounded-full bg-brandRed/10 flex items-center justify-center" data-id="element-372">
            <Zap className="w-5 h-5 text-brandRed" data-id="element-373" />
          </div>
          <div data-id="element-374">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-375">
              Speed Rush
            </div>
            <div className="text-sm font-bold text-ink" data-id="element-376">
              Available on many items
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
const AudienceSplitSection = () => <section className="py-24 bg-gray50" data-id="element-377">
    <div className="max-w-7xl mx-auto px-4" data-id="element-378">
      <div className="text-center mb-14" data-id="element-379">
        <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-380">
          Two Clear Paths
        </span>
        <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3" data-id="element-381">
          Choose the Right Path
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto" data-id="element-382">
        {/* Card 1: Dealers (primary) */}
        <div className="bg-ink rounded-xl p-10 md:p-12 shadow-2xl flex flex-col h-full border border-white/5 relative overflow-hidden" data-id="element-383">
          <div className="absolute top-0 right-0 w-40 h-40 bg-brandRed/10 rounded-full blur-3xl" data-id="element-384"></div>
          <div className="relative z-10" data-id="element-385">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brandRed bg-brandRed/10 px-3 py-1.5 rounded-full mb-5" data-id="element-386">
              Primary Wholesale Path
            </div>
            <h3 className="text-3xl font-heading font-bold text-white mb-5" data-id="element-387">
              For Dealers & Resellers
            </h3>
            <p className="text-gray-300 leading-relaxed text-lg mb-8" data-id="element-388">
              Sell custom uniforms and apparel under your own brand with
              wholesale pricing, private-label production, combined quantity
              pricing, and direct production support.
            </p>

            <ul className="flex flex-col gap-3.5 mb-10" data-id="element-389">
              {['Protect your brand', 'Protect your margins', 'Quote full team packages more competitively', 'Use T1 production support behind the scenes'].map((benefit, i) => <li key={i} className="flex items-start gap-3 text-gray-100 text-base" data-id="element-390">
                  <CheckCircle2 className="w-5 h-5 text-brandRed shrink-0 mt-0.5" data-id="element-391" />
                  <span data-id="element-392">{benefit}</span>
                </li>)}
            </ul>

            <Link to="/wholesale-apply" className="bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-6 rounded-sm text-center transition-colors w-full block text-base shadow-lg" data-id="element-393">
              Apply for Wholesale Access
            </Link>
          </div>
        </div>

        {/* Card 2: Teams */}
        <div className="bg-white rounded-xl p-10 md:p-12 shadow-lg flex flex-col h-full border border-gray200" data-id="element-394">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray100 px-3 py-1.5 rounded-full mb-5 self-start" data-id="element-395">
            Routed Through Suppliers
          </div>
          <h3 className="text-3xl font-heading font-bold text-ink mb-5" data-id="element-396">
            For Teams, Leagues & Organizations
          </h3>
          <p className="text-gray-600 leading-relaxed text-lg mb-8" data-id="element-397">
            Looking for custom uniforms or team gear? Submit your information
            and we can help connect you with an approved supplier who can manage
            your order.
          </p>

          <div className="bg-gray50 border-l-4 border-charcoal p-5 mb-10 flex-grow rounded-r" data-id="element-398">
            <p className="text-base text-gray-700 leading-relaxed" data-id="element-399">
              <strong className="text-ink block mb-1.5" data-id="element-400">Important Note</strong>
              This site is not a retail/direct-to-customer ordering site. Teams
              and organizations are routed through the supplier/dealer network.
            </p>
          </div>

          <Link to="/find-a-supplier" className="bg-white border-2 border-charcoal hover:bg-charcoal hover:text-white text-charcoal font-bold py-3.5 px-6 rounded-sm text-center transition-colors w-full text-base" data-id="element-401">
            Find a Supplier
          </Link>
        </div>
      </div>
    </div>
  </section>;
const KitBuilderSection = () => <section className="py-24 lg:py-32 bg-ink relative overflow-hidden" data-id="element-402">
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-403">
      <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-brandRed/10 blur-[150px]" data-id="element-404"></div>
      <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-white/5 blur-[120px]" data-id="element-405"></div>
    </div>

    <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-406">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-407">
        <div className="lg:col-span-5 flex flex-col gap-6" data-id="element-408">
          <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-409">
            <span className="w-8 h-px bg-brandRed" data-id="element-410"></span>
            3D Uniform Builder
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight tracking-tight" data-id="element-411">
            Build Your Uniform.
            <br data-id="element-412" />
            See It Instantly in 3D.
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed" data-id="element-413">
            Choose styles, colors, patterns, fabrics, and options before
            submitting your design for wholesale production support.
          </p>

          <ul className="flex flex-col gap-4 mt-2" data-id="element-414">
            {[{
            icon: Eye,
            text: 'Preview designs instantly'
          }, {
            icon: Palette,
            text: 'Select colors and patterns'
          }, {
            icon: Layers,
            text: 'Build sport-specific uniforms'
          }, {
            icon: ClipboardCheck,
            text: 'Submit designs for quote/support'
          }, {
            icon: Clock,
            text: 'Reduce back-and-forth before production'
          }].map((feature, i) => <li key={i} className="flex items-center gap-4 text-gray-100 font-medium text-base" data-id="element-415">
                <div className="w-11 h-11 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0" data-id="element-416">
                  <feature.icon className="w-5 h-5 text-brandRed" data-id="element-417" />
                </div>
                <span data-id="element-418">{feature.text}</span>
              </li>)}
          </ul>

          <div className="mt-6" data-id="element-419">
            <Link to="/kit-builder" className="inline-flex items-center gap-2 bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-7 rounded-sm transition-colors shadow-lg shadow-brandRed/20 text-base group" data-id="element-420">
              Start Your Design — See It Instantly in 3D
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-421" />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-7 relative" data-id="element-422">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-423">
            <BuilderVisual theme="dark" data-id="element-424" />
          </motion.div>
        </div>
      </div>
    </div>
  </section>;
const SeasonalSportsSection = () => {
  const [activeTab, setActiveTab] = useState('featured');
  const tabs = [{
    id: 'featured',
    label: 'Featured Now'
  }, {
    id: 'fall',
    label: 'Fall'
  }, {
    id: 'winter',
    label: 'Winter'
  }, {
    id: 'spring',
    label: 'Spring'
  }];
  const getSportsForTab = (tabId: string): string[] => {
    switch (tabId) {
      case 'featured':
        return ['football', 'soccer', 'baseball', 'softball', 'basketball', 'volleyball'];
      case 'fall':
        return ['soccer', 'baseball', 'softball', 'basketball', 'volleyball'];
      case 'winter':
        return ['basketball', 'volleyball', 'hockey', 'wrestling', 'groupwear'];
      case 'spring':
        return ['baseball', 'softball', 'basketball', 'track-and-field'];
      default:
        return [];
    }
  };
  const activeSports = getSportsForTab(activeTab);
  const primarySport = activeSports[0];
  const secondarySports = activeSports.slice(1);
  const getCat = (slug: string) => categories.find(c => c.slug === slug);
  const primaryCat = getCat(primarySport);
  return <section className="py-24 bg-white" data-id="element-425">
      <div className="max-w-7xl mx-auto px-4" data-id="element-426">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12" data-id="element-427">
          <div data-id="element-428">
            <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-429">
              In Season
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3 mb-4" data-id="element-430">
              Featured Seasonal Uniforms
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl" data-id="element-431">
              Start with the sports currently in season, then browse upcoming
              fall, winter, and spring categories.
            </p>
          </div>

          <div className="inline-flex bg-gray100 p-1 rounded-lg self-start md:self-auto overflow-x-auto max-w-full hide-scrollbar" data-id="element-432">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn('relative px-5 py-2.5 text-sm font-bold rounded-md transition-colors whitespace-nowrap', activeTab === tab.id ? 'text-ink' : 'text-gray-500 hover:text-charcoal')} data-id="element-433">
                {activeTab === tab.id && <motion.div layoutId="activeTab" className="absolute inset-0 bg-white rounded-md shadow-sm border border-gray200" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-434" />}
                <span className="relative z-10" data-id="element-435">{tab.label}</span>
              </button>)}
          </div>
        </div>

        <AnimatePresence mode="wait" data-id="element-436">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="grid lg:grid-cols-3 gap-5" data-id="element-437">
            {/* Primary featured card */}
            {primaryCat && <div className="lg:col-span-2 relative group rounded-xl overflow-hidden bg-charcoal border border-gray200 aspect-[4/3] lg:aspect-auto lg:h-[520px]" data-id="element-438">
                {primaryCat.image ? <img src={primaryCat.image} alt={primaryCat.name} className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" data-id="element-439" /> : <SportIconCard category={primaryCat} large data-id="element-440" />}
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" data-id="element-441"></div>
                <div className="absolute bottom-0 left-0 p-8 md:p-10 w-full" data-id="element-442">
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brandRed bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4" data-id="element-443">
                    Featured Now
                  </div>
                  <h3 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-444">
                    {primaryCat.name} Uniforms
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-445">
                    <Link to="/kit-builder" className="bg-brandRed hover:bg-brandRedDark text-white font-bold py-3 px-6 rounded-sm transition-colors text-base" data-id="element-446">
                      Start a {primaryCat.name} Design
                    </Link>
                    <Link to={`/category/${primarySport}`} className="bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-bold py-3 px-6 rounded-sm transition-colors text-base" data-id="element-447">
                      Shop {primaryCat.name} Uniforms
                    </Link>
                  </div>
                </div>
              </div>}

            {/* Secondary sports */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-1 lg:grid-rows-4 lg:h-[520px]" data-id="element-448">
              {secondarySports.slice(0, 4).map(slug => {
              const cat = getCat(slug);
              if (!cat) return null;
              return <Link key={slug} to={`/category/${slug}`} className="group relative rounded-xl overflow-hidden bg-gray100 border border-gray200 aspect-square lg:aspect-auto flex items-end" data-id="element-449">
                    {cat.image ? <>
                        <img src={cat.image} alt={cat.name} className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" data-id="element-450" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" data-id="element-451"></div>
                      </> : <SportIconCard category={cat} data-id="element-452" />}
                    <div className="relative z-10 flex items-center justify-between w-full p-4" data-id="element-453">
                      <h4 className="text-lg font-heading font-bold text-white" data-id="element-454">
                        {cat.name}
                      </h4>
                      <ChevronRight className="w-5 h-5 text-white opacity-70 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" data-id="element-455" />
                    </div>
                  </Link>;
            })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
};
// Renders an icon-based card for categories without proper imagery
const SportIconCard = ({
  category,
  large = false
}: {
  category: (typeof categories)[0];
  large?: boolean;
}) => {
  const Icon = category.icon;
  return <div className="absolute inset-0 bg-gradient-to-br from-charcoal to-ink flex items-center justify-center" data-id="element-456">
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
      backgroundSize: large ? '40px 40px' : '20px 20px'
    }} data-id="element-457"></div>
      {Icon && <Icon className={cn('text-brandRed/40', large ? 'w-32 h-32' : 'w-12 h-12')} strokeWidth={1.5} data-id="element-458" />}
    </div>;
};
const DealerValueSection = () => {
  const benefits = [{
    icon: ShieldCheck,
    title: 'Private-label production',
    desc: 'No-logo production protects your brand identity. We stay behind the scenes.'
  }, {
    icon: TrendingUp,
    title: 'Wholesale pricing',
    desc: 'Built specifically to protect and maximize dealer margins.'
  }, {
    icon: Layers,
    title: 'Combined quantity pricing',
    desc: 'Mix and match items to reach higher discount tiers across your package.'
  }, {
    icon: Zap,
    title: 'Speed Rush production',
    desc: 'Fast turnarounds available on many no-button items.'
  }, {
    icon: PackageSearch,
    title: 'Increased production capacity',
    desc: 'Reliable fulfillment for large volume orders.'
  }, {
    icon: Scissors,
    title: 'Additional fabric options',
    desc: 'More choices for texture, stretch, and performance.'
  }, {
    icon: Clock,
    title: 'USA-time support',
    desc: 'Dedicated support team available 9am–10pm EST.'
  }, {
    icon: Truck,
    title: 'Drop-ship-friendly',
    desc: 'Direct fulfillment options to simplify your logistics.'
  }, {
    icon: CheckSquare,
    title: 'Quality control',
    desc: 'Rigorous checks at multiple production levels.'
  }];
  return <section className="py-24 bg-gray50 border-y border-gray200" data-id="element-459">
      <div className="max-w-7xl mx-auto px-4" data-id="element-460">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-461">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-462">
            Built for Dealers
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3 mb-6 leading-tight" data-id="element-463">
            Built for Dealers Who Need Better Margins and Fewer Production
            Headaches
          </h2>
          <p className="text-lg text-gray-600" data-id="element-464">
            Custom Uniform Supplier helps dealers and resellers sell custom
            apparel under their own brand while relying on T1 production,
            support, and fulfillment behind the scenes.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" data-id="element-465">
          {benefits.map((benefit, i) => <div key={i} className="bg-white p-7 rounded-xl border border-gray200 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 group" data-id="element-466">
              <div className="w-14 h-14 rounded-lg bg-gray50 border border-gray100 flex items-center justify-center mb-5 group-hover:bg-brandRed/5 group-hover:border-brandRed/20 transition-colors" data-id="element-467">
                <benefit.icon className="w-7 h-7 text-charcoal group-hover:text-brandRed transition-colors" strokeWidth={1.75} data-id="element-468" />
              </div>
              <h3 className="text-xl font-bold text-ink mb-2.5" data-id="element-469">
                {benefit.title}
              </h3>
              <p className="text-base text-gray-600 leading-relaxed" data-id="element-470">
                {benefit.desc}
              </p>
            </div>)}
        </div>

        {/* Combined Quantity Callout */}
        <div className="bg-ink rounded-2xl p-10 md:p-12 text-white flex flex-col md:flex-row items-start md:items-center gap-8 shadow-2xl relative overflow-hidden" data-id="element-471">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brandRed/10 rounded-full blur-3xl" data-id="element-472"></div>
          <div className="w-20 h-20 rounded-2xl bg-brandRed flex items-center justify-center shrink-0 relative z-10" data-id="element-473">
            <TrendingUp className="w-10 h-10 text-white" strokeWidth={2} data-id="element-474" />
          </div>
          <div className="relative z-10" data-id="element-475">
            <h4 className="text-2xl md:text-3xl font-heading font-bold mb-3" data-id="element-476">
              How Combined Quantity Pricing Works
            </h4>
            <p className="text-gray-200 leading-relaxed text-lg max-w-3xl" data-id="element-477">
              If a dealer orders 100 shirts and 100 shorts, they can qualify for
              200-piece pricing instead of two separate 100-piece tiers. This
              helps dealers quote full packages more competitively.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
const QualityProductionSection = () => {
  const checks = [{
    icon: Palette,
    title: 'Art Review',
    desc: 'Detailed artwork review and setup.'
  }, {
    icon: Eye,
    title: 'Color Matching',
    desc: 'Accurate color reproduction.'
  }, {
    icon: Zap,
    title: 'Heat Press Checks',
    desc: 'Verified application quality.'
  }, {
    icon: Layers,
    title: 'Sorting',
    desc: 'Organized by team, size, and roster.'
  }, {
    icon: Scissors,
    title: 'Finishing',
    desc: 'Final inspection of every piece.'
  }, {
    icon: Truck,
    title: 'Drop-Ship Fulfillment',
    desc: 'Ready to ship to your customer.'
  }];
  return <section className="py-24 bg-white" data-id="element-478">
      <div className="max-w-7xl mx-auto px-4" data-id="element-479">
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-480">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-481">
            Quality Control
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3 mb-6 leading-tight" data-id="element-482">
            Production Support Built Around Accuracy
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed" data-id="element-483">
            Extensive quality control at multiple levels helps protect your
            artwork, colors, sizing, sorting, and finishing before orders ship.
          </p>
        </div>

        {/* Approval Flow */}
        <div className="max-w-5xl mx-auto mb-20" data-id="element-484">
          <div className="text-center mb-8" data-id="element-485">
            <span className="text-sm font-bold uppercase tracking-wider text-gray-400" data-id="element-486">
              Approval Process
            </span>
          </div>
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 relative" data-id="element-487">
            <div className="hidden md:block absolute top-1/2 left-[10%] right-[10%] h-0.5 bg-gray200 -z-0 -translate-y-1/2" data-id="element-488"></div>

            {[{
            step: '1',
            title: 'Artwork Approval',
            desc: 'Initial art review'
          }, {
            step: '2',
            title: 'Mini Approval',
            desc: 'Small sample check'
          }, {
            step: '3',
            title: 'Full-Size Sample Approval',
            desc: 'Final production review'
          }].map((phase, i) => <div key={i} className="flex-1 bg-white border-2 border-ink rounded-xl px-6 py-5 flex items-center gap-4 shadow-sm relative z-10" data-id="element-489">
                <div className="w-10 h-10 rounded-full bg-brandRed text-white text-base font-bold flex items-center justify-center shrink-0" data-id="element-490">
                  {phase.step}
                </div>
                <div data-id="element-491">
                  <div className="font-bold text-ink text-base" data-id="element-492">
                    {phase.title}
                  </div>
                  <div className="text-sm text-gray-500 mt-0.5" data-id="element-493">
                    {phase.desc}
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Quality control cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-494">
          {checks.map((check, i) => <div key={i} className="bg-gray50 border border-gray200 rounded-xl p-5 hover:border-brandRed/30 hover:bg-white transition-colors" data-id="element-495">
              <div className="w-10 h-10 rounded-lg bg-white border border-gray200 flex items-center justify-center mb-4" data-id="element-496">
                <check.icon className="w-5 h-5 text-brandRed" data-id="element-497" />
              </div>
              <h4 className="font-bold text-ink text-base mb-1.5" data-id="element-498">
                {check.title}
              </h4>
              <p className="text-sm text-gray-600 leading-snug" data-id="element-499">{check.desc}</p>
            </div>)}
        </div>
      </div>
    </section>;
};
const CategoryGridSection = () => <section className="py-24 bg-gray50 border-t border-gray200" data-id="element-500">
    <div className="max-w-7xl mx-auto px-4" data-id="element-501">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12" data-id="element-502">
        <div data-id="element-503">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-504">
            Wholesale Catalog
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3" data-id="element-505">
            Browse All Categories
          </h2>
        </div>
        <Link to="/products" className="text-base font-bold text-charcoal hover:text-brandRed transition-colors flex items-center gap-1.5 group" data-id="element-506">
          View Full Catalog
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-507" />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-508">
        {categories.map(cat => <Link key={cat.slug} to={`/category/${cat.slug}`} className="group bg-white border border-gray200 rounded-xl overflow-hidden hover:border-charcoal transition-all hover:-translate-y-1 hover:shadow-xl flex flex-col" data-id="element-509">
            <div className="aspect-square bg-gray100 relative overflow-hidden" data-id="element-510">
              {cat.image ? <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-511" /> : <IconCategoryCard category={cat} data-id="element-512" />}
            </div>
            <div className="p-4 bg-white" data-id="element-513">
              <div className="flex items-center justify-between mb-1" data-id="element-514">
                <span className="font-bold text-ink text-base" data-id="element-515">{cat.name}</span>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brandRed transition-colors" data-id="element-516" />
              </div>
              {cat.shortLabel && <p className="text-sm text-gray-500 leading-tight" data-id="element-517">
                  {cat.shortLabel}
                </p>}
            </div>
          </Link>)}
      </div>
    </div>
  </section>;
// Icon-based card for categories without proper imagery (Fishing, Hockey, Soccer, Accessories)
const IconCategoryCard = ({
  category
}: {
  category: (typeof categories)[0];
}) => {
  const Icon = category.icon;
  return <div className="w-full h-full bg-gradient-to-br from-ink to-charcoal flex items-center justify-center relative overflow-hidden" data-id="element-518">
      <div className="absolute inset-0 opacity-[0.07]" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
      backgroundSize: '20px 20px'
    }} data-id="element-519"></div>
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-brandRed/10 rounded-full blur-2xl" data-id="element-520"></div>
      {Icon && <div className="relative z-10 w-20 h-20 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm flex items-center justify-center" data-id="element-521">
          <Icon className="w-10 h-10 text-white" strokeWidth={1.5} data-id="element-522" />
        </div>}
      <div className="absolute bottom-3 left-3 text-[10px] font-bold uppercase tracking-wider text-white/50" data-id="element-523">
        Wholesale
      </div>
    </div>;
};
const HowItWorksSection = () => {
  const steps = [{
    title: 'Choose a sport or product',
    desc: 'Browse our extensive wholesale catalog.'
  }, {
    title: 'Start your design in Kit Builder',
    desc: 'Customize colors, patterns, and fabrics in 3D.'
  }, {
    title: 'Submit for wholesale quote/support',
    desc: 'Send your design to our team for review.'
  }, {
    title: 'Review art, sizing, and production details',
    desc: 'Finalize specs with our USA-based support.'
  }, {
    title: 'Approve the final production steps',
    desc: 'Sign off on artwork and samples.'
  }, {
    title: 'Receive private-label custom gear',
    desc: 'Ready for your customer or team.'
  }];
  return <section className="py-24 bg-white" data-id="element-524">
      <div className="max-w-7xl mx-auto px-4" data-id="element-525">
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-526">
          <span className="text-sm font-bold uppercase tracking-wider text-brandRed" data-id="element-527">
            Process
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-ink mt-3 mb-5 leading-tight" data-id="element-528">
            From Design to Production Support
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed" data-id="element-529">
            Built for dealers and resellers who want T1 production behind their
            own customer-facing brand.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-530">
          {steps.map((step, i) => <div key={i} className="relative flex flex-col gap-4 p-8 rounded-xl border border-gray200 bg-gray50 hover:bg-white hover:shadow-md transition-all" data-id="element-531">
              <div className="text-7xl font-heading font-extrabold text-gray200 absolute top-4 right-6 pointer-events-none leading-none" data-id="element-532">
                0{i + 1}
              </div>
              <div className="w-12 h-12 rounded-lg bg-ink text-white flex items-center justify-center font-bold text-lg z-10" data-id="element-533">
                {i + 1}
              </div>
              <div className="z-10" data-id="element-534">
                <h3 className="text-xl font-bold text-ink mb-2.5" data-id="element-535">
                  {step.title}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed" data-id="element-536">
                  {step.desc}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const FooterCTABanner = () => <section className="bg-ink py-20 border-b border-white/10 relative overflow-hidden" data-id="element-537">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-full bg-brandRed/5 blur-3xl" data-id="element-538"></div>
    <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-539">
      <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight" data-id="element-540">
        Ready to Elevate Your Brand?
      </h2>
      <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-541">
        Start your first design in 3D or apply for wholesale access to unlock
        dealer pricing and private-label production.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-542">
        <Link to="/kit-builder" className="w-full sm:w-auto bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-8 rounded-sm transition-colors shadow-lg text-base" data-id="element-543">
          Start Your Design
        </Link>
        <Link to="/wholesale-apply" className="w-full sm:w-auto bg-transparent border-2 border-white hover:bg-white hover:text-ink text-white font-bold py-3.5 px-8 rounded-sm transition-colors text-base" data-id="element-544">
          Apply for Wholesale Access
        </Link>
      </div>
    </div>
  </section>;